import React, { FC } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { getAlgoliaResults } from '@algolia/autocomplete-js';
import moment from 'moment/moment';
import {
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_BREED_INDEX,
  algoliaPrecisionSettings,
  listingAttributesToHighlightSearchSuggestions,
  listingAttributesToRetrieveSearchSuggestions,
  publiclySearchableAllListingsAttributes,
} from '../../../utils/constants';
import '@algolia/autocomplete-theme-classic';
import useCurrentUser from '../../../utils/hooks/useCurrentUser';
import routes from '../../../utils/routes';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';
import useRequestLocation from '../../../hooks/useRequestLocation';
import Autocomplete from './Autocomplete';
import SuggestedHit from './SuggestedHit';

const currentTime = moment().utc().format('X');

const SearchSuggestions: FC<RouteComponentProps> = ({
  location,
}) => {
  const { currentUser } = useCurrentUser();

  const { searchClient } = useAlgoliaSearchClientLite(currentUser?.public_key);

  const {
    searchLocation,
  } = useRequestLocation();

  const debouncePromise = (fn, time) => {
    let timerId: NodeJS.Timeout | undefined;

    return function debounced(...args) {
      if (timerId) {
        clearTimeout(timerId);
      }

      return new Promise((resolve) => {
        timerId = setTimeout(() => resolve(fn(...args)), time);
      });
    };
  };

  const debounced = debouncePromise((items: any) => Promise.resolve(items), 500);

  const { search } = location;
  const initialQuery = new URLSearchParams(search);
  const initialSearchQueryTerm = initialQuery.get('query') || '';

  return (
    <Autocomplete
      placeholder="Find Puppies..."
      detachedMediaQuery="none"
      openOnFocus={false}
      onSubmit={({ state, event }) => {
        event.preventDefault();
        if (document.querySelectorAll('.react-search').length === 0
            || document.querySelectorAll('.js-redirect-on-autocomplete-submit').length > 0
        ) {
          const { query } = state;
          // Redirect if no Algolia form on page or if Algolia page has search box of its own
          window.location.href = routes.webSearch(query);
        }
      }}
      // TODO: Figure out how to type this
      // @ts-ignore-next-line
      getSources={({ query }) => {
        return debounced([
          {
            sourceId: 'suggestedBreeds',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_BREED_INDEX!,
                    params: {
                      facetFilters: ['visible_by:group/all'],
                      hitsPerPage: 10,
                    },
                    query,
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return item.route || '';
            },
            templates: {
              header() {
                return (
                  <h2 className="aa-suggestions-category">Breeds</h2>
                );
              },
              item({ item }) {
                return (
                  <SuggestedHit
                    hit={item}
                    displayKey={['name', 'alternative_breed_names']}
                  />
                );
              },
              footer() {
                return (
                  <div className="aa-show-more">
                    <a href={routes.WEB_BREEDS_INDEX}>See more breeds</a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedPuppyAds',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
                    params: {
                      facetFilters: ['listings_type:puppy', 'status:published', 'availability:available'],
                      numericFilters: [
                        [
                          `expires_at > ${currentTime}`,
                          'can_expire = 0',
                        ],
                        [
                          'isVisibleOnPublicSearch = 1',
                        ],
                      ],
                      hitsPerPage: 10,
                      aroundRadius: 'all',
                      // aroundPrecision will be ignored if aroundLatLng is empty
                      aroundPrecision: algoliaPrecisionSettings,
                      aroundLatLng: searchLocation || '',
                      // NOTE: This WILL break and cause a 400 response from Algolia if any of
                      // these attributes are not included in the searchableAttributes for the
                      // index being searched
                      restrictSearchableAttributes: publiclySearchableAllListingsAttributes,
                      attributesToRetrieve: listingAttributesToRetrieveSearchSuggestions,
                      attributesToHighlight: listingAttributesToHighlightSearchSuggestions,
                    },
                    query,
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return item.route || '';
            },
            templates: {
              header() {
                return (
                  <h2 className="aa-suggestions-category">Puppies</h2>
                );
              },
              item({ item }) {
                return (
                  <SuggestedHit
                    hit={item}
                    displayKey={['global_hit_title']}
                  />
                );
              },
              footer() {
                return (
                  <div className="aa-show-more">
                    Can't find a puppy? Try our
                    {' '}
                    <a href={routes.webSearch(query)}>Advanced Puppy Search</a>
                  </div>
                );
              },
            },
          },
          {
            sourceId: 'suggestedStudAds',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
                    params: {
                      facetFilters: ['listings_type:stud', 'status:published', 'availability:available'],
                      numericFilters: [
                        [
                          `expires_at > ${currentTime}`,
                          'can_expire = 0',
                        ],
                        [
                          'isVisibleOnPublicSearch = 1',
                        ],
                      ],
                      hitsPerPage: 10,
                      aroundRadius: 'all',
                      // aroundPrecision will be ignored if aroundLatLng is empty
                      aroundPrecision: algoliaPrecisionSettings,
                      aroundLatLng: searchLocation || '',
                      // NOTE: This WILL break and cause a 400 response from Algolia if any of
                      // these attributes are not included in the searchableAttributes for the
                      // index being searched
                      restrictSearchableAttributes: publiclySearchableAllListingsAttributes,
                      attributesToRetrieve: listingAttributesToRetrieveSearchSuggestions,
                      attributesToHighlight: listingAttributesToHighlightSearchSuggestions,
                    },
                    query,
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return item.route || '';
            },
            templates: {
              header() {
                return (
                  <h2 className="aa-suggestions-category">Stud Dogs</h2>
                );
              },
              item({ item }) {
                return (
                  <SuggestedHit
                    hit={item}
                    displayKey={['global_hit_title']}
                  />
                );
              },
              footer() {
                return (
                  <div className="aa-show-more">
                    Can't find a stud dog? Try our
                    {' '}
                    <a href={routes.webStudSearch(query)}>Advanced Stud Dog Search</a>
                  </div>
                );
              },
            },
          },
        ]);
      }}
      emptyQuerySearchOptions={() => (
        <div className="aa-dropdown-menu">
          <div className="aa-dropdown-menus">
            <h2 className="aa-suggestions-category">Breeds</h2>
            <div className="aa-show-more">
              <a href={routes.WEB_BREEDS_INDEX}>Search All Breeds</a>
            </div>
          </div>
          <div className="aa-dropdown-menus">
            <h2 className="aa-suggestions-category">Puppies</h2>
            <div className="aa-show-more">
              <a href={routes.webSearch('')}>Advanced Puppy Search</a>
            </div>
          </div>
          <div className="aa-dropdown-menus">
            <h2 className="aa-suggestions-category">Stud Dogs</h2>
            <div className="aa-show-more">
              <a href={routes.webStudSearch('')}>Advanced Stud Dog Search</a>
            </div>
          </div>
        </div>
      )}
      initialQuery={initialSearchQueryTerm}
    />
  );
};

export default SearchSuggestions;
